import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import SelectableCard from 'tio-ui/components/selectable-card';
import Text from 'tio-ui/components/text';
import formatCentsToDollars from 'tio-common/helpers/format-cents-to-dollars';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import LoanMeta from 'tio-employee/components/slr/loan-verification/loan-meta';
import { becomesBalance, type LoanChoice } from 'tio-employee/utils/slr/loan-verification';
import type { ExistingLoan, HeldLoan } from 'tio-data/requests/loan-verification';

interface HeldLoanCardSignature {
  Args: {
    /** A recorded or applied loan shows the server's verdict and cannot be changed. */
    loan: HeldLoan;
    /** One-based place among the loans on the page, for the "Loan N of M" label. */
    position: number;
    /** How many loans the page lists, the M in "Loan N of M". */
    total: number;
    /** The account's loans, in the server's order; the picker lists every one. */
    existingLoans: ExistingLoan[];
    /** The member's pick on this page; absent until they choose. */
    choice?: LoanChoice;
    /** Loans strict 1:1 keeps from this held loan; the picker shows them "In use" and disabled. */
    unavailableLoanIds: Set<string>;
    /** Shows "Which existing loan does this replace?" in place of the New / Refresh buttons. */
    isPickerOpen: boolean;
    /** Summary rows and loans answered before the page opened: the decision shows, nothing changes it. */
    isReadOnly: boolean;
    /** Set when the server refused this loan's decision. */
    errorMessage?: string;
    /** "New Loan to Add" was pressed. */
    onChooseNew?: () => void;
    /** "Refresh Existing Loan" was pressed; the caller opens this card's picker. */
    onOpenPicker?: () => void;
    /** The picker's Cancel was pressed; nothing was chosen. */
    onCancelPicker?: () => void;
    /** A loan the picker offered was picked; never called for one shown "In use". */
    onChooseExisting?: (existingLoanId: string) => void;
    /** "Change" was pressed; the caller clears this loan's decision. */
    onChange?: () => void;
  };
}

/**
 * One held loan on the "Verify Your Loans" page: what the document shows, then the New Loan to
 * Add / Refresh Existing Loan choice, the replace picker, or the decision with its Change control.
 */
export default class HeldLoanCard extends Component<HeldLoanCardSignature> {
  pickerHeadingId = `loan-picker-${guidFor(this)}`;

  // A recorded or applied loan carries no local choice; its decision line comes from the server.
  get choice(): LoanChoice | undefined {
    const loan = this.args.loan;
    if (!this.isApplied && !this.isRecorded) return this.args.choice;
    if (loan.verdict === 'different') return { kind: 'new' };
    const replacedLoanId = this.isApplied ? loan.appliedLoanId : loan.answeredLoanId;
    const replacedId = loan.verdict === 'same' ? replacedLoanId : null;
    return replacedId && this.args.existingLoans.some((existing) => existing.loanId === replacedId)
      ? { kind: 'replace', existingLoanId: replacedId }
      : undefined;
  }

  get isApplied() {
    return this.args.loan.state === 'applied';
  }

  get isRecorded() {
    return this.args.loan.state === 'answered';
  }

  // No line rather than a guessed recorder when the server sends no time or no attribution
  get recordedOnKey() {
    const { answeredAt, answeredOnBehalf } = this.args.loan;
    if (!this.isRecorded || !answeredAt || answeredOnBehalf === null) return undefined;
    return answeredOnBehalf
      ? 'slr.loan_verification.recorded_on_behalf'
      : 'slr.loan_verification.recorded_on';
  }

  // The recorded answer replaces a loan the page no longer lists, so the line cannot name it.
  get replacesUnlistedLoan() {
    return this.isRecorded && this.args.loan.verdict === 'same' && !this.replaced;
  }

  get replaced() {
    const choice = this.choice;
    if (choice?.kind !== 'replace') return undefined;
    return this.args.existingLoans.find((loan) => loan.loanId === choice.existingLoanId);
  }

  // A loan answered before this page opened reads its verdict off the server, never a local choice.
  get stripKind() {
    if (this.isApplied || this.isRecorded) {
      return this.args.loan.verdict === 'same' ? 'replace' : 'new';
    }
    if (!this.args.choice) return 'needs-review';
    return this.args.choice.kind === 'replace' ? 'replace' : 'new';
  }

  get stripClass() {
    return this.stripKind === 'needs-review'
      ? 'tio-bg-warning-soft tio-fg-warning-body'
      : 'tio-bg-success-soft tio-fg-success-body';
  }

  get replacedPreview() {
    return this.replaced ? becomesBalance(this.args.loan, this.replaced.loanId) : null;
  }

  // The balance line needs both ends; missing either, the decision names the loan alone.
  // After apply the replaced loan already carries its new balance, so there is no "from" to show.
  get showsBalanceChange() {
    return (
      !this.isApplied && this.replaced?.currentBalance !== null && this.replacedPreview !== null
    );
  }

  get isOpenForChoice() {
    return !this.args.isReadOnly && this.args.loan.state === 'held' && !this.args.choice;
  }

  get hasExistingLoans() {
    return this.args.existingLoans.length > 0;
  }

  get hasMeta() {
    return !!(this.typeText(this.args.loan) || this.args.loan.disbursedOn);
  }

  // A held loan reads as its document states it; an existing loan has only its label
  typeText = (loan: { typeLabel: string | null; rawType?: string | null }) =>
    loan.rawType ?? loan.typeLabel ?? '';

  isUnavailable = (existing: ExistingLoan) => this.args.unavailableLoanIds.has(existing.loanId);

  previewFor = (existing: ExistingLoan) => becomesBalance(this.args.loan, existing.loanId);

  hasPreview = (existing: ExistingLoan) => this.previewFor(existing) !== null;

  chooseNew = () => this.args.onChooseNew?.();

  openPicker = () => this.args.onOpenPicker?.();

  cancelPicker = () => this.args.onCancelPicker?.();

  chooseExisting = (existingLoanId: string) => this.args.onChooseExisting?.(existingLoanId);

  change = () => this.args.onChange?.();

  <template>
    <Card
      @elevation="none"
      @padding="none"
      class="tio-col-flush w-full overflow-hidden"
      data-test-held-loan={{@loan.holdId}}
      data-test-held-loan-state={{this.stripKind}}
    >
      <div
        class="tio-row-between px-4 py-2 uppercase tracking-wide tio-text-xs-semibold
          {{this.stripClass}}"
      >
        <span data-test-held-loan-strip>
          {{#if (eq this.stripKind "replace")}}
            {{t "slr.loan_verification.replace_strip"}}
          {{else if (eq this.stripKind "new")}}
            {{t "slr.loan_verification.new_strip"}}
          {{else}}
            {{t "slr.loan_verification.needs_review"}}
          {{/if}}
        </span>
        <span data-test-held-loan-position>
          {{t "slr.loan_verification.loan_position" position=@position total=@total}}
        </span>
      </div>
      <div class="tio-col p-4">
        <div class="tio-row-start-between w-full">
          <div class="tio-col-extra-tight tio-fill-min">
            <Text @weight="semibold" data-test-held-loan-name>{{@loan.name}}</Text>
            <Text @size="sm" @variant="muted" data-test-held-loan-meta>
              <LoanMeta @label={{this.typeText @loan}} @disbursedOn={{@loan.disbursedOn}} />
              {{#if @loan.servicerName}}
                {{#if this.hasMeta}}
                  {{t "slr.loan_verification.reported_by_separated" servicer=@loan.servicerName}}
                {{else}}
                  {{t "slr.loan_verification.reported_by" servicer=@loan.servicerName}}
                {{/if}}
              {{/if}}
            </Text>
          </div>
          <Text @weight="semibold" data-test-held-loan-balance>
            {{formatCentsToDollars @loan.currentBalance}}
          </Text>
        </div>

        {{#if this.isRecorded}}
          <div class="tio-row-between flex-wrap w-full" data-test-held-loan-decision>
            <Text @size="sm">
              {{#if this.replaced}}
                {{t "slr.loan_verification.recorded_replaces_line" name=this.replaced.name}}
              {{else if this.replacesUnlistedLoan}}
                {{t "slr.loan_verification.recorded_replaces_unlisted"}}
              {{else}}
                {{t "slr.loan_verification.recorded_new_line"}}
              {{/if}}
            </Text>
          </div>
          {{#if this.recordedOnKey}}
            <Text @size="sm" @variant="muted" data-test-held-loan-recorded-on>
              {{t this.recordedOnKey date=(safeFormatDate @loan.answeredAt)}}
            </Text>
          {{/if}}
        {{else if this.choice}}
          <div class="tio-row-between flex-wrap w-full" data-test-held-loan-decision>
            <Text @size="sm">
              {{#if this.replaced}}
                {{#if this.showsBalanceChange}}
                  {{t
                    "slr.loan_verification.replaces_line"
                    name=this.replaced.name
                    from=(formatCentsToDollars this.replaced.currentBalance)
                    to=(formatCentsToDollars this.replacedPreview)
                  }}
                {{else}}
                  {{t "slr.loan_verification.replaces_line_unpriced" name=this.replaced.name}}
                {{/if}}
              {{else}}
                {{t "slr.loan_verification.new_line"}}
              {{/if}}
            </Text>
            {{#unless @isReadOnly}}
              <Button
                @appearance="link"
                @size="sm"
                @testHook="loan-verification__change-{{@loan.holdId}}"
                data-test-held-loan-change
                {{on "click" this.change}}
              >
                {{t "slr.loan_verification.change"}}
              </Button>
            {{/unless}}
          </div>
        {{/if}}

        {{#if this.isOpenForChoice}}
          {{#if @isPickerOpen}}
            <div class="tio-col-tight w-full" data-test-loan-picker={{@loan.holdId}}>
              <Text @weight="semibold" id={{this.pickerHeadingId}}>
                {{t "slr.loan_verification.picker_heading"}}
              </Text>
              <Text @size="sm" @variant="muted">
                {{t "slr.loan_verification.picker_helper"}}
              </Text>
              <div class="tio-col-tight" role="radiogroup" aria-labelledby={{this.pickerHeadingId}}>
                {{#each @existingLoans as |existing|}}
                  <SelectableCard
                    @value={{existing.loanId}}
                    @label={{existing.name}}
                    @description=""
                    @isSelected={{false}}
                    @disabled={{this.isUnavailable existing}}
                    @onChange={{this.chooseExisting}}
                  >
                    <:label>
                      <span class="tio-row-between w-full">
                        <span class="tio-col-extra-tight">
                          <span>{{existing.name}}</span>
                          <span class="tio-text-xs-normal tio-fg-muted">
                            <LoanMeta
                              @label={{this.typeText existing}}
                              @disbursedOn={{existing.disbursedOn}}
                            />
                          </span>
                        </span>
                        {{#if (this.isUnavailable existing)}}
                          <Chip @size="sm" data-test-loan-picker-in-use={{existing.loanId}}>
                            {{t "slr.loan_verification.in_use"}}
                          </Chip>
                        {{else if (this.hasPreview existing)}}
                          <span data-test-loan-picker-becomes={{existing.loanId}}>
                            {{t
                              "slr.loan_verification.becomes"
                              amount=(formatCentsToDollars (this.previewFor existing))
                            }}
                          </span>
                        {{/if}}
                      </span>
                    </:label>
                  </SelectableCard>
                {{/each}}
              </div>
              <div>
                <Button
                  @appearance="link"
                  @size="sm"
                  @testHook="loan-verification__cancel-{{@loan.holdId}}"
                  data-test-loan-picker-cancel
                  {{on "click" this.cancelPicker}}
                >
                  {{t "slr.loan_verification.cancel"}}
                </Button>
              </div>
            </div>
          {{else}}
            <div class="tio-col-tight w-full">
              <Text @size="sm" data-test-held-loan-question>
                {{t "slr.loan_verification.question"}}
              </Text>
              <div class="tio-row flex-wrap">
                <Button
                  @intent="primary"
                  @testHook="loan-verification__new-{{@loan.holdId}}"
                  data-test-held-loan-choose-new
                  {{on "click" this.chooseNew}}
                >
                  {{t "slr.loan_verification.new_loan"}}
                </Button>
                <Button
                  @appearance="outlined"
                  @isDisabled={{not this.hasExistingLoans}}
                  @testHook="loan-verification__refresh-{{@loan.holdId}}"
                  data-test-held-loan-choose-refresh
                  {{on "click" this.openPicker}}
                >
                  {{t "slr.loan_verification.refresh_existing"}}
                </Button>
              </div>
            </div>
          {{/if}}
        {{/if}}

        {{#if @errorMessage}}
          <Text @size="sm" @variant="error" role="alert" data-test-held-loan-error>
            {{@errorMessage}}
          </Text>
        {{/if}}
      </div>
    </Card>
  </template>
}
